import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { FaTrashAlt } from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMenu } from "../../context/MenuContext";

const API_URL = "https://code.gurusport.in/api/wishlists";
const CART_API_URL = "https://code.gurusport.in/api/cart";

const WishlistDrawer = ({ isOpen, onClose }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const navigate = useNavigate();

  // ✅ FIX: Prevent destructuring error if useMenu() returns undefined
  const menuContext = useMenu() || {}; // <-- Added fallback to avoid crash
  const { fetchMenuCounts } = menuContext;

  // ✅ Fetch Wishlist Items
  const fetchWishlist = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;

      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      console.log("💖 Wishlist API Response:", res.data);

      if (res.data && Array.isArray(res.data.data)) {
        const items = res.data.data.map((item) => {
          const product = item.products?.data?.[0] || {};
          const size = item.size?.data?.[0] || null;
          const color = item.colors?.data?.[0] || null;

          return {
            id: item.id,
            productId: product.id,
            name: product.title || "Unnamed Product",
            image: product.image || "#",
            costPrice: product.cost_price || null,
            sellingPrice: product.selling_price || null,
            size: size,
            color: color,
          };
        });

        setWishlistItems(items);
        fetchMenuCounts && fetchMenuCounts(); // ✅ Call only if function exists
      } else {
        setWishlistItems([]);
        fetchMenuCounts && fetchMenuCounts();
      }
    } catch (error) {
      console.error("❌ Error fetching wishlist:", error);
    }
  };

  // ✅ Remove item from Wishlist
  const handleRemoveItem = async (id) => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setWishlistItems((prev) => prev.filter((item) => item.id !== id));
      fetchMenuCounts && fetchMenuCounts(); // ✅ Safe call
    } catch (error) {
      console.error("❌ Error removing wishlist item:", error);
    }
  };

  // ✅ Move item to Cart
  const handleMoveToCart = async (item) => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;

      const cartItemPayload = {
        product_id: item.productId,
        size_id: item.size?.id,
        color_id: item.color?.id,
        quantity: 1,
      };

      await axios.post(CART_API_URL, cartItemPayload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      await handleRemoveItem(item.id); // Remove from wishlist
    } catch (error) {
      console.error("❌ Error moving item to cart:", error);
    }
  };

  // ✅ Clear Wishlist
  const handleClearWishlist = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) return;

      await Promise.all(
        wishlistItems.map((item) =>
          axios.delete(`${API_URL}/${item.id}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          })
        )
      );

      setWishlistItems([]);
      fetchMenuCounts && fetchMenuCounts(); // ✅ Safe call
    } catch (error) {
      console.error("❌ Error clearing wishlist:", error);
    }
  };

  useEffect(() => {
    if (isOpen) fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={onClose}
        ></div>
      )}

      {/* Wishlist Drawer */}
      {/* <div
        className={`fixed top-0 right-0 h-[80vh] w-[430px] bg-white z-50 shadow-lg transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      > */}
      <div
        className={`fixed top-0 right-0 h-[80vh] w-full sm:w-[350px] md:w-[400px] lg:w-[430px] bg-white z-50 shadow-lg transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="font-bold text-sm uppercase">
              My Wishlist ({wishlistItems.length})
            </h2>
            <button onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Wishlist Items */}
          <div className="p-4 space-y-4">
            {wishlistItems.length > 0 ? (
              wishlistItems.map((item) => (
                <div key={item.id} className="flex gap-4 border-b pb-3">
                  {/* <img
                    src={item.image}
                    alt={item.name}
                    className="w-[80px] h-[100px] object-cover rounded"
                  /> */}
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-[60px] h-[80px] sm:w-[70px] sm:h-[90px] md:w-[80px] md:h-[100px] object-cover rounded"
                  />

                  <div className="flex-1 text-sm">
                    {/* <h4 className="font-semibold">{item.name}</h4> */}
                    <h4 className="font-semibold text-sm sm:text-base md:text-base">
                      {item.name}
                    </h4>

                    {item.size && (
                      <p className="text-gray-500">Size: {item.size.name}</p>
                    )}
                    {item.color && (
                      <p className="text-gray-500">Color: {item.color.name}</p>
                    )}

                    <div className="mt-2">
                      {item.costPrice && (
                        <p className="text-gray-500 text-sm">
                          Cost Price: ₹{parseFloat(item.costPrice).toFixed(2)}
                        </p>
                      )}
                      {item.sellingPrice && (
                        <p className="font-semibold text-green-700 text-sm sm:text-base md:text-base">
                          Selling Price: ₹
                          {parseFloat(item.sellingPrice).toFixed(2)}
                        </p>
                      )}
                    </div>

                    {/* ✅ Move to Cart Button */}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleMoveToCart(item)}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Move to Cart
                      </button>
                    </div>
                  </div>

                  {/* Remove Item Button */}
                  <button
                    className="self-start mt-2 sm:mt-0"
                    onClick={() => handleRemoveItem(item.id)}
                  >
                    <FaTrashAlt className="text-red-600 hover:text-red-800" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">
                Your wishlist is empty
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t text-sm pb-6 mt-auto">
            <div className="flex justify-between items-center">
              <span>{wishlistItems.length} items</span>
              {wishlistItems.length > 0 && (
                <button
                  onClick={handleClearWishlist}
                  className="text-sm text-red-600 hover:underline"
                >
                  Clear Wishlist
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WishlistDrawer;
